import { useCallback, useMemo } from "react";

import { IPetItemResponse } from "../services/pet-list/pet-list.interface";
import { MIXPANEL_EVENTS } from "../config/mixPanelEvents";
import { MODALS } from "../config/params";
import { PAGES_PATH } from "../navigation/params";
import { useModal } from "../context/ModalContext";
import { useNavigate } from "react-router-dom";
import { useOverdueInvoiceStore } from "./redux-hooks/overdue-invoice-store/overdue-invoice.store";
import { usePetStore } from "./redux-hooks/pet-store/pet-store.service";
import { userActivityLogger } from "../utils/mixpanel";
import { logPrequalEvent } from "../shared/containers/PrequalificationContainer/prequalAnalytics";

export interface UsePrequalFunnelOptions {
  source: string;
}

export interface UsePrequalFunnelResult {
  eligiblePets: IPetItemResponse[];
  canEnter: boolean;
  start: () => void;
}

export const isPrequalEligiblePet = (pet: IPetItemResponse): boolean =>
  (pet.speciesName === "Dog" || pet.speciesName === "Cat") && !pet.isWellnessplan;

/**
 * TotalCare prequalification funnel orchestrator, modeled on
 * useWellnessFunnel. Unlike the wellness funnel there is no pet-birthdate
 * gate (the credit check needs the owner's DOB, collected in the flow) and
 * no upfront getPrequalificationInfo call — the routed container owns the
 * already-prequalified gate so every entry point shares one code path.
 *
 * @param {UsePrequalFunnelOptions} options Source label for analytics.
 * @return {UsePrequalFunnelResult} Eligible pets, a canEnter flag, and the
 *   `start` handler consumers bind to their CTA.
 */
export const usePrequalFunnel = (
  options: UsePrequalFunnelOptions,
): UsePrequalFunnelResult => {
  const { source } = options;
  const { petList } = usePetStore();
  const { openModal } = useModal();
  const {
    cancelledWellnessPetList,
    pausedWellnessPetList,
    pendingPaymentPetList,
  } = useOverdueInvoiceStore();
  const navigate = useNavigate();

  const eligiblePets = useMemo<IPetItemResponse[]>(
    () => (petList ?? []).filter(isPrequalEligiblePet),
    [petList],
  );

  const canEnter = eligiblePets.length > 0;

  // Same delinquency gate as useWellnessFunnel: a user with unpaid plans
  // must not start a credit check for a plan they cannot purchase.
  const blockedByDelinquency = useCallback((): boolean => {
    if (cancelledWellnessPetList.length > 0) {
      userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_BOOKING_BLOCKED, {
        delinquency_state: "cancelled",
        entry_point: source,
        num_affected_pets: pendingPaymentPetList.length,
      });
      openModal(MODALS.PENDING_INVOICE_CANCELLED_ALERT_MODAL);
      return true;
    }
    if (pausedWellnessPetList.length > 0) {
      userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_BOOKING_BLOCKED, {
        delinquency_state: "paused",
        entry_point: source,
        num_affected_pets: pendingPaymentPetList.length,
      });
      openModal(MODALS.PENDING_INVOICE_PAUSED_ALERT_MODAL, PAGES_PATH.PET_CARE);
      return true;
    }
    return false;
  }, [
    cancelledWellnessPetList,
    openModal,
    pausedWellnessPetList,
    pendingPaymentPetList,
    source,
  ]);

  const start = useCallback(() => {
    // PRD props note: `entry_point` mirrors `referred_from` (kept for dashboard
    // continuity). `is_returning_prequal`/`prequal_status_at_entry` aren't
    // knowable at the CTA — the container's gate query resolves them a step
    // later and reports returning users via prequal_status_recognized.
    logPrequalEvent(MIXPANEL_EVENTS.PREQUAL_STARTED, {
      referred_from: source,
      entry_point: source,
    });

    if (blockedByDelinquency()) return;

    if (!petList || petList.length === 0) {
      openModal(MODALS.ADD_A_PET, source);
      return;
    }

    if (eligiblePets.length === 0) {
      return;
    }

    if (eligiblePets.length === 1) {
      navigate(`${PAGES_PATH.PREQUALIFICATION}?id=${eligiblePets[0].id}`);
      return;
    }

    openModal(MODALS.PREQUAL_SELECT_PET, source);
  }, [blockedByDelinquency, eligiblePets, navigate, openModal, petList, source]);

  return {
    eligiblePets,
    canEnter,
    start,
  };
};
